<template>
  <div class="popUpWrap">
    <section class="popup dark assessment">
      <div class="topArea">
        <div class="exit" data-test="sessionExpiredClose" @click.stop="examSession.dismiss" />
      </div>
      <div class="scrollY">
        <div class="textArea flex">
          <h5>{{ t('skillCertify-session-expired-title') }}</h5>
          <p>{{ t('skillCertify-session-expired-description') }}</p>
        </div>
        <p v-if="isSaveFailed" class="errorMessage" data-test="sessionExpiredSaveFail"><span></span>{{ t('skillCertify-session-expired-save-fail') }}</p>
        <div class="btnArea">
          <LazySafetyRequestButton
              type="button"
              tag="button"
              class="btn blueBtn"
              :safety-click="onClickLogin"
              data-test="sessionExpiredLogin"
          >
            {{ t('account-login') }}
          </LazySafetyRequestButton>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// #2260: the session expired on the taking screen. [X] only hides this popup (the candidate keeps
// working; submit/finish re-open it). [로그인] first preserves the answer — multi-file code to S3
// under the retained uuid, everything else as a local draft (examAnswerDraft) — and only then leaves
// for the login page with RedirectUrl → assessment START page, whose existing checks + Continue bring
// the candidate back to the taking screen (START resume re-issues the seat and token).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#imports';
import { useLearningStore } from '~/store/learning/learningStore';
import { useLearningAssessmentStore } from '~/store/learning/assessment/learningAssessmentStore';
import { useUserStore } from '~/store/user/userStore';
import { useLearningDirectoryService } from '~/composables/service/useLearningDirectoryService';
import { saveExamAnswerDraft } from '~/composables/examAnswerDraft';
import { useExamSession } from '~/composables/useExamSession';
import { setRedirectUrlCookie } from '~/utils/redirectUrlCookie';
import { RouteName } from '~~/package/enum/RouteName';
import { TaskType } from '~~/package/mapper/learning/model/TaskType';
import { SignType, SignUIType } from '~/components/Sign/enum/SignType';

const { t } = useI18n();
const router = useRouter();
const examSession = useExamSession();
const learningStore = useLearningStore();
const assessmentStore = useLearningAssessmentStore();
const userStore = useUserStore();
const learningDirectoryService = useLearningDirectoryService();

const isSaveFailed = ref(false);

const preserveAnswer = async (): Promise<boolean> => {
    if (learningStore.task.type === TaskType.MULTI_FILE_CODE) {
        return await learningDirectoryService.preserveMultipleCode();
    }
    try {
        saveExamAnswerDraft();
        return true;
    } catch (e) {
        console.error(e);
        return false;
    }
};

const onClickLogin = async (): Promise<void> => {
    isSaveFailed.value = false;
    if (!await preserveAnswer()) {
        isSaveFailed.value = true;
        return;
    }

    setRedirectUrlCookie(router.resolve({
        name: RouteName.LearningProSkillAssureAssessmentId,
        params: { assessmentId: assessmentStore.proSkillAssure.assessmentId },
    }));
    // Full-page navigation: the taking page's onBeforeRouteLeave blocks /sign for in-app routing.
    window.location.assign(router.resolve({
        path: '/sign',
        query: { uiType: SignUIType.LOGIN, type: SignType.CANDIDATE, userEmail: userStore.userV2.email },
    }).href);
};
</script>
